import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  type CreditAnalysisSituation,
  CreditAnalysisStatus,
  creditAnalysisApi,
} from '@/api/commercial/credit-analysis';
import { CreateCreditAnalysisDialog } from '@/components/forms/commercial/credit-analysis/create';
import { CreditAnalysisTable } from '@/components/table/commercial/credit-analysis-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableSkeleton } from '@/components/ui/skeleton';

type SituationFilter = 'Todos' | CreditAnalysisSituation;
type StatusFilter = 'Todos' | CreditAnalysisStatus;

const SITUATION_OPTIONS: { value: SituationFilter; label: string }[] = [
  { value: 'Todos', label: 'Todos' },
  { value: 'APPROVED', label: 'Aprovada' },
  { value: 'PARTIALLY_APPROVED', label: 'Aprovada com ressalva' },
  { value: 'DECLINED', label: 'Reprovada' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'Todos', label: 'Todos' },
  { value: CreditAnalysisStatus.NOT_STARTED, label: 'Não iniciada' },
  { value: CreditAnalysisStatus.PENDING_EXTERNAL_DOCS, label: 'Aguardando documentação' },
  { value: CreditAnalysisStatus.PENDING_ANALYSIS, label: 'Aguardando Análise' },
  { value: CreditAnalysisStatus.IN_ANALYSIS, label: 'Em análise' },
  { value: CreditAnalysisStatus.APPROVED, label: 'Em dia (Aprovada)' },
  { value: CreditAnalysisStatus.PARTIALLY_APPROVED, label: 'Em dia (Aprovada com ressalva)' },
  { value: CreditAnalysisStatus.DOC_DECLINED, label: 'Documentação recusada' },
  { value: CreditAnalysisStatus.DECLINED, label: 'Reprovada' },
  { value: CreditAnalysisStatus.EXPIRED, label: 'Vencida' },
];

export function CreditAnalysisListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states from URL
  const [filterClient, setFilterClient] = useState(searchParams.get('client') || '');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterSituation, setFilterSituation] = useState<SituationFilter>(
    (searchParams.get('situation') as SituationFilter) || 'Todos',
  );
  const [filterStatus, setFilterStatus] = useState<StatusFilter>(
    (searchParams.get('status') as StatusFilter) || 'Todos',
  );
  const [filterRequestStart, setFilterRequestStart] = useState(
    searchParams.get('request_start') || '',
  );
  const [filterRequestEnd, setFilterRequestEnd] = useState(searchParams.get('request_end') || '');
  const [filterAnalysisStart, setFilterAnalysisStart] = useState(
    searchParams.get('analysis_start') || '',
  );
  const [filterAnalysisEnd, setFilterAnalysisEnd] = useState(
    searchParams.get('analysis_end') || '',
  );

  const page = Number(searchParams.get('page')) || 1;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['credit-analysis', Object.fromEntries(searchParams)],
    queryFn: () => {
      const params: Record<string, string | number> = {};
      const client = searchParams.get('client');
      const situation = searchParams.get('situation');
      const status = searchParams.get('status');
      const requestStart = searchParams.get('request_start');
      const requestEnd = searchParams.get('request_end');
      const analysisStart = searchParams.get('analysis_start');
      const analysisEnd = searchParams.get('analysis_end');
      const pageParam = searchParams.get('page');

      if (client) params.search = client;
      if (situation && situation !== 'Todos') params.situation = situation;
      if (status && status !== 'Todos') params.status = status;
      if (requestStart) params.request_start = requestStart;
      if (requestEnd) params.request_end = requestEnd;
      if (analysisStart) params.analysis_start = analysisStart;
      if (analysisEnd) params.analysis_end = analysisEnd;
      if (pageParam) params.page = Number(pageParam);

      return creditAnalysisApi.findMany(params);
    },
  });

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('page', '1');

    if (filterClient) params.set('client', filterClient);
    if (filterSituation && filterSituation !== 'Todos') params.set('situation', filterSituation);
    if (filterStatus && filterStatus !== 'Todos') params.set('status', filterStatus);
    if (filterRequestStart) params.set('request_start', filterRequestStart);
    if (filterRequestEnd) params.set('request_end', filterRequestEnd);
    if (filterAnalysisStart) params.set('analysis_start', filterAnalysisStart);
    if (filterAnalysisEnd) params.set('analysis_end', filterAnalysisEnd);

    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilterClient('');
    setFilterSituation('Todos');
    setFilterStatus('Todos');
    setFilterRequestStart('');
    setFilterRequestEnd('');
    setFilterAnalysisStart('');
    setFilterAnalysisEnd('');
    setSearchParams(new URLSearchParams());
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Erro ao carregar análises de crédito</p>
        <Button onClick={() => refetch()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Análises de crédito</h1>
          <p className="text-muted-foreground">Visualize e gerencie as análises de crédito</p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-black text-white hover:bg-gray-800"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Análise
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>Defina os critérios e clique em "Pesquisar"</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div>
                <Input
                  label="Cliente"
                  placeholder="Nome do cliente"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-700">Situação</span>
                <Select
                  value={filterSituation}
                  onValueChange={(value) => setFilterSituation(value as SituationFilter)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    {SITUATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-700">Status</span>
                <Select
                  value={filterStatus}
                  onValueChange={(value) => setFilterStatus(value as StatusFilter)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Input
                  label="Período da solicitação (início)"
                  type="date"
                  value={filterRequestStart}
                  onChange={(e) => setFilterRequestStart(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Período da solicitação (fim)"
                  type="date"
                  value={filterRequestEnd}
                  onChange={(e) => setFilterRequestEnd(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Período de análise (início)"
                  type="date"
                  value={filterAnalysisStart}
                  onChange={(e) => setFilterAnalysisStart(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Período de análise (fim)"
                  type="date"
                  value={filterAnalysisEnd}
                  onChange={(e) => setFilterAnalysisEnd(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                <Search className="h-4 w-4 mr-2" />
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : data ? (
        <CreditAnalysisTable data={data} currentPage={page} onPageChange={handlePageChange} />
      ) : null}

      <CreateCreditAnalysisDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={() => {
          refetch();
          setShowCreateDialog(false);
        }}
      />
    </motion.div>
  );
}
